// PER-NODE HORIZONTAL SPACING — the column solve (TGCLOUD-1908).
//
// Auto spacing used to derive ONE pitch from the widest label on the chart
// (TGCLOUD-1395), so a single long label widened every gap. This module turns
// the engine's uniform layout into per-node spacing WITHOUT touching the engine:
// it reads the final, unmapped render model, derives the constraints the drawn
// chart must satisfy, and solves a small linear program for the drawn position
// of every person. The result is a per-row piecewise map (colUnits.js) that
// buildRenderModel applies at node creation, so every union, junction, lane,
// satellite and edge is rebuilt from the mapped positions by the code that
// builds them today.
//
// The variables are PEOPLE, not columns (Tim's second QA family). A column map
// moved every row's people in a shared engine column together, so a label in
// one row opened a label-free couple in another row that only happened to sit
// in the same columns. Here each row is free, and rows are tied to each other
// only where a line connects them. Every person is bounded below by their
// position at the FLOOR pitch; the objective is the chart's width first and
// the total displacement from the floor second, so a chart whose nodes need
// nothing beyond the floor stays exactly the floor chart, and every other
// chart is as narrow as its constraints allow with as few people moved.
//
// What the solve preserves is exactly what the engine already achieved in the
// uniform layout (Phase 0 measurement, PLAN-tgcloud-1908): the naive map —
// each gap sized from its two footprints alone — kinked a single-child drop
// whenever the child's fractional column split its parents' gap into unequal
// halves, and unevened sibships. So the constraints are:
//
//   • PAIRS      two people adjacent on a row sit at least
//                (need(A) + need(B)) / 2 apart, where a node's need is its own
//                label-block width plus the symbol margin (autoSpacing.js), and
//                never closer than the floor pitch per engine slot between them;
//   • STRAIGHT   a single-child drop that is straight today stays straight
//                (the child keeps its proportion of its parents' span, or the
//                junction's band when the union is asym-anchored);
//   • MIDRANGE   a multi-child union sitting on its sibship's midrange today
//                stays there, and one inside the bar stays inside it;
//   • EVEN       a sibship whose consecutive gaps are equal today stays even;
//   • ORDER      every x-order the drawn lines depend on is kept: between a
//                row's people and its droppers — unions and single parents —
//                (marriage bars and drops), and between a row's people and
//                droppers and the people of the row beneath (the bars and child
//                drops at the gap's midline, and the label blocks hanging into
//                it, which keep their clearance where they have it). Consecutive
//                pairs of those sorted sequences are enough (order is
//                transitive), and they are added lazily, only when a solve
//                breaks one — a drop that slid across a child column crossed
//                the sibling bar beneath it (C368, +3 crossings);
//   • BAND       a hub junction the engine left clamped — its children's
//                midrange lies past the band that keeps a junction off a
//                partner's own column, so the drop stops 0.75 symbol widths
//                from the partner and lands off the children (G-1-01515: 33 and
//                18 "too close to the drop", 36 and 37 off-centre) — has its
//                sibship moved into the band, so the rebuilt junction lands on
//                the children. Tim's ruling, 2026-10-15: fold it into the
//                solve. The one rule that improves on the uniform chart rather
//                than preserving it; dropped, with the solve re-run, if it
//                cannot be met with everything else;
//   • CLEAR      a union's drop stays clear of each partner's label block, and
//                of the block of any node between the partners on the row, by
//                the clearance the uniform chart gave it (half the margin for
//                the widest label). The pair rule alone sizes a couple's gap
//                from the MEAN of the two needs, which keeps the two blocks off
//                each other but lets the drop at the midpoint run through the
//                wider one (Tim's first QA family).
//
// Every equality and clearance is derived from a property the unmapped model
// already has, so the uniform layout scaled by (engine pitch / floor) is always
// feasible. Pinned nodes take no part: a pin overrides its node's position,
// exactly as it does today.
//
// Fail-safe: anything unexpected (fewer than two people, a solver failure, a
// solution that misses a constraint) returns null, and the chart draws with the
// uniform map it has always used.

import { config, BASE_NODE_GAP, isGameteDonorNode } from './config.js';
import { solveLP } from './lpSimplex.js';

// childPath's own straight-drop threshold (path.js): a drop is drawn straight
// when the union and the child are within 1 px.
const STRAIGHT_EPS = 1;
// Tolerance for verifying the solved positions against the extracted constraints.
const CHECK_EPS = 1e-6;
const negTerms = (terms) => terms.map(([k, coef]) => [k, -coef]);

const sibKey = (n) =>
  n.fatherId != null || n.motherId != null
    ? `${n.fatherId}|${n.motherId}`
    : null;

// Replicates buildRenderModel's extra-spouse union anchor (the 2nd+ spouse on one
// side of a 3+-spouse hub): the union sits at `previousSpouse.x ± step`, clamped
// to t ∈ [0.05, 0.95] along the hub→spouse line. Returns null when neither
// partner is such a hub, so the caller can tell a plain midpoint from an anchor.
function extraSpouseAnchor(p1, p2, nodesById, step) {
  for (const [hub, extra] of [
    [p1, p2],
    [p2, p1]
  ]) {
    const spouses = (hub.spouseIds || [])
      .map((sid) => nodesById.get(sid))
      .filter(Boolean);
    if (spouses.length < 3) continue;
    const side = (
      extra.x < hub.x
        ? spouses.filter((s) => s.x < hub.x).sort((a, b) => b.x - a.x)
        : spouses.filter((s) => s.x >= hub.x).sort((a, b) => a.x - b.x)
    ).filter((s) => Number.isFinite(s.x));
    const i = side.indexOf(extra);
    if (i < 1) continue;
    const prev = side[i - 1];
    const dir = extra.x > hub.x ? 1 : -1;
    const targetX = prev.x + dir * step;
    const lineT =
      extra.x !== hub.x ? (targetX - hub.x) / (extra.x - hub.x) : 0.5;
    const t = Math.max(0.05, Math.min(0.95, lineT));
    return {
      hub,
      extra,
      prev,
      dir,
      t,
      clamped: t !== lineT,
      lineT,
      x: hub.x + t * (extra.x - hub.x)
    };
  }
  return null;
}

/**
 * Derive the variables (people) and the constraints of the solve from an
 * UNMAPPED render model. Exported for tests; solveColumns is the entry point.
 *
 * Terms are [variableIndex, coefficient] over the drawn positions P_i of the
 * variable people (`vars`); `base[i]` is P_i's lower bound, the position at
 * the floor pitch.
 */
export function extractColumnConstraints(model, opts = {}) {
  if (!model || !Array.isArray(model.nodes) || !model.nodesById) return null;
  const needs = opts.needs || {};
  const customPositions = opts.customPositions || null;
  const nodeGap = Number(opts.nodeGap ?? config.NODE_GAP) || BASE_NODE_GAP;
  const grid = Number(opts.grid ?? config.GRID_SIZE * 2) || 0;
  const sym = Number(opts.symbolSize ?? config.SYMBOL_SIZE) || 155;
  const floor = Math.min(
    Number(opts.floor ?? BASE_NODE_GAP) || BASE_NODE_GAP,
    nodeGap
  );
  const minOff = (config.ASYM_JUNCTION_PARENT_CLEARANCE ?? 0.75) * sym;
  // The BAND rule runs only on a chart a label has widened past the floor. A
  // chart that needs nothing beyond the floor stays the floor chart, byte for
  // byte (perNodeHSpacingParity's floor degeneracy), as Tim was told when he
  // chose the rule: it acts where the solve acts, which is where labels are.
  const labelled = nodeGap > floor;
  const step = config.EXTRA_SPOUSE_CHILD_X_STEP ?? 500;
  // A need is a label-block width plus this margin (autoSpacing.js); a line
  // clears a block by half of it, which is what the widest label gets today.
  const margin =
    Number(opts.marginSym ?? config.AUTO_H_MARGIN_SYM ?? 1.0) * sym;
  const lineClear = margin / 2;
  const NODE = config.NODE_TYPE.NODE;
  const UNION = config.NODE_TYPE.UNION;

  const isPinned = (n) =>
    !!(customPositions && n.uid != null && customPositions.has(n.uid));

  const persons = [];
  for (const n of model.nodes) {
    if (!n || n.kind !== NODE || n.hidden) continue;
    if (isGameteDonorNode(n)) continue;
    if (!Number.isFinite(n.virtualX) || !Number.isFinite(n.virtualY)) continue;
    if (!Number.isFinite(n.x)) continue;
    persons.push(n);
  }
  if (persons.length < 2) return null;
  const rowOf = (n) => Math.round(n.virtualY);

  // Variables: the unpinned people. Position at the floor pitch is the lower
  // bound; the engine's own pixel (== n.x for an unpinned person) is what the
  // identity check compares against.
  const vars = persons.filter((n) => !isPinned(n));
  if (vars.length < 2) return null;
  const vi = new Map(vars.map((n, i) => [n.pid, i]));
  // Same expression and operand order as Family.convertToCoordinates at the
  // floor pitch, so a chart that needs nothing beyond the floor is exactly it.
  const base = vars.map((n) => n.virtualX * floor + grid);
  const uniformX = (n) => n.virtualX * nodeGap + grid;
  const usable = (n) => !!(n && vi.has(n.pid));
  const idx = (n) => vi.get(n.pid);
  const needOf = (n) => {
    const v = Number(needs[String(n.pid)]);
    return Number.isFinite(v) && v > 0 ? Math.max(v, floor) : floor;
  };

  const rows = new Map();
  for (const n of persons) {
    const r = rowOf(n);
    if (!rows.has(r)) rows.set(r, []);
    rows.get(r).push(n);
  }
  for (const arr of rows.values()) {
    arr.sort((a, b) => a.virtualX - b.virtualX || a.pid - b.pid);
  }

  const constraints = [];
  const add = (kind, terms, op, rhs) =>
    constraints.push({ kind, terms, op, rhs });
  const addSoft = (kind, terms, op, rhs) =>
    constraints.push({ kind, terms, op, rhs, soft: true });
  const neg = negTerms;

  // PAIRS: adjacent people on a row — the mean of their needs, and never
  // closer than the floor pitch per engine slot between them.
  for (const arr of rows.values()) {
    for (let i = 1; i < arr.length; i++) {
      const A = arr[i - 1];
      const B = arr[i];
      if (!usable(A) || !usable(B)) continue;
      const dv = B.virtualX - A.virtualX;
      if (!(dv > 0)) continue;
      add(
        'pair',
        [
          [idx(B), 1],
          [idx(A), -1]
        ],
        'ge',
        Math.max(dv * floor, (needOf(A) + needOf(B)) / 2)
      );
    }
  }

  // UNIONS: straight drops, midrange junctions, in-bar unions. Each union's
  // drawn x as a linear expression for the ORDER and CLEAR passes below.
  const unionExprs = [];
  for (const u of model.nodes) {
    if (!u || u.kind !== UNION) continue;
    const p1 = model.nodesById.get(u.parent1Id);
    const p2 = model.nodesById.get(u.parent2Id);
    if (!usable(p1) || !usable(p2)) continue;
    if (rowOf(p1) !== rowOf(p2)) continue;
    if (!Number.isFinite(u.x)) continue;
    const allKids = (u.childrenIds || []).map((id) => model.nodesById.get(id));
    const kids = allKids.filter(usable);
    if (!kids.length) continue;
    // The engine's target is the midrange of EVERY child; the rule below
    // moves that target, so it needs every child as a variable.
    const allKidsUsable = allKids.every(usable);
    const left = p1.x <= p2.x ? p1 : p2;
    const right = left === p1 ? p2 : p1;
    const a = idx(left);
    const b = idx(right);
    const span = right.x - left.x;
    if (!(span > 0)) continue;
    const plainMid = Math.abs(u.x - (left.x + right.x) / 2) < STRAIGHT_EPS;
    const kidXs = kids.map((k) => k.x);
    const lo = Math.min(...kidXs);
    const hi = Math.max(...kidXs);
    const kLo = idx(kids.find((k) => k.x === lo));
    const kHi = idx(kids.find((k) => k.x === hi));
    const target = kids.length === 1 ? kidXs[0] : (lo + hi) / 2;
    const targetTerms =
      kids.length === 1
        ? [[kLo, 1]]
        : [
            [kLo, 0.5],
            [kHi, 0.5]
          ];
    const onTarget = Math.abs(u.x - target) < STRAIGHT_EPS;
    const midTerms = [
      [a, 0.5],
      [b, 0.5]
    ];

    if (plainMid) {
      unionExprs.push({
        terms: midTerms,
        constant: 0,
        x: u.x,
        left,
        right,
        row: rowOf(p1)
      });
    } else if (u.asymBand && onTarget) {
      unionExprs.push({
        terms: targetTerms,
        constant: 0,
        x: u.x,
        left,
        right,
        row: rowOf(p1)
      });
    } else if (u.asymBand && allKidsUsable && labelled) {
      // Clamped today: the junction sits at the band's edge, off its
      // children. Ask for the children's midrange inside the band, so the
      // rebuilt junction (buildRenderModel clamps it to the same band from
      // the mapped positions) lands on them. Soft — see solveColumns — and
      // everything derived from this union (its clearances, its place in the
      // order census) is soft with it, so dropping the rule is exactly the
      // solve without it.
      addSoft('bandIn', [...targetTerms, [a, -1]], 'ge', minOff);
      addSoft('bandIn', [[b, 1], ...neg(targetTerms)], 'ge', minOff);
      unionExprs.push({
        terms: targetTerms,
        constant: 0,
        x: u.x,
        left,
        right,
        row: rowOf(p1),
        soft: true
      });
    }

    if (onTarget) {
      if (plainMid) {
        add('straight', [...midTerms, ...neg(targetTerms)], 'eq', 0);
      } else if (u.asymBand) {
        // The junction follows its target while the target stays inside the
        // band, so keeping the band is what keeps the drop straight.
        if (span > 2 * minOff) {
          add('band', [...targetTerms, [a, -1]], 'ge', minOff);
          add('band', [[b, 1], ...neg(targetTerms)], 'ge', minOff);
        } else {
          add('straight', [...midTerms, ...neg(targetTerms)], 'eq', 0);
        }
      } else {
        const anchor = extraSpouseAnchor(p1, p2, model.nodesById, step);
        if (
          anchor &&
          usable(anchor.prev) &&
          Math.abs(anchor.x - u.x) < STRAIGHT_EPS
        ) {
          const { hub, extra, prev, dir, t, clamped } = anchor;
          const h = idx(hub);
          const e = idx(extra);
          const p = idx(prev);
          if (!clamped) {
            // target = P_prev + dir·step, i.e. the same offset as today.
            add('anchor', [...targetTerms, [p, -1]], 'eq', target - prev.x);
            unionExprs.push({
              terms: [[p, 1]],
              constant: dir * step,
              x: u.x,
              left,
              right,
              row: rowOf(p1)
            });
            // …and the anchor stays unclamped: 0.05·D ≤ N ≤ 0.95·D with
            // N = dir·(P_prev − P_hub) + step and D = dir·(P_extra − P_hub).
            add(
              'anchorBand',
              [
                [p, dir],
                [h, -dir + 0.05 * dir],
                [e, -0.05 * dir]
              ],
              'ge',
              -step
            );
            add(
              'anchorBand',
              [
                [e, 0.95 * dir],
                [h, -0.95 * dir + dir],
                [p, -dir]
              ],
              'ge',
              step
            );
          } else {
            // Clamped today: the union is a fixed proportion of the span.
            add('anchor', [...targetTerms, [h, -(1 - t)], [e, -t]], 'eq', 0);
            unionExprs.push({
              terms: [
                [h, 1 - t],
                [e, t]
              ],
              constant: 0,
              x: u.x,
              left,
              right,
              row: rowOf(p1)
            });
          }
        }
      }
    } else if (
      kids.length >= 2 &&
      plainMid &&
      u.x >= lo - STRAIGHT_EPS &&
      u.x <= hi + STRAIGHT_EPS
    ) {
      // Off the midrange but inside the bar today: keep it inside the bar.
      add('inSpan', [...midTerms, [kLo, -1]], 'ge', 0);
      add('inSpan', [[kHi, 1], ...neg(midTerms)], 'ge', 0);
    }
  }

  // SINGLE PARENTS: a person with children but no partner drawn drops a line
  // from their own symbol (the child edge's `from` is the person). They get the
  // union treatment — a single child stays under them, a sibship keeps them on
  // its midrange or inside its bar — and they drop in the order census below.
  // (Tim's third QA family: 49→48, 17→31, 41→40, 45→46 and 8→33 all kinked
  // while only union drops were held straight.)
  const singleKids = new Map();
  for (const e of model.edges || []) {
    if (!e || e.kind !== 'child' || !vi.has(e.from)) continue;
    const c = model.nodesById.get(e.to);
    if (!usable(c)) continue;
    if (!singleKids.has(e.from)) singleKids.set(e.from, []);
    singleKids.get(e.from).push(c);
  }
  for (const [pid, kids] of singleKids) {
    const parent = model.nodesById.get(pid);
    if (!usable(parent) || !kids.length) continue;
    const pTerms = [[idx(parent), 1]];
    const kidXs = kids.map((k) => k.x);
    const lo = Math.min(...kidXs);
    const hi = Math.max(...kidXs);
    const kLo = idx(kids.find((k) => k.x === lo));
    const kHi = idx(kids.find((k) => k.x === hi));
    const target = kids.length === 1 ? kidXs[0] : (lo + hi) / 2;
    const targetTerms =
      kids.length === 1
        ? [[kLo, 1]]
        : [
            [kLo, 0.5],
            [kHi, 0.5]
          ];
    if (Math.abs(parent.x - target) < STRAIGHT_EPS) {
      add('straight', [...pTerms, ...neg(targetTerms)], 'eq', 0);
    } else if (
      kids.length >= 2 &&
      parent.x >= lo - STRAIGHT_EPS &&
      parent.x <= hi + STRAIGHT_EPS
    ) {
      add('inSpan', [...pTerms, [kLo, -1]], 'ge', 0);
      add('inSpan', [[kHi, 1], ...neg(pTerms)], 'ge', 0);
    }
    unionExprs.push({
      terms: pTerms,
      constant: 0,
      x: parent.x,
      left: parent,
      right: parent,
      row: rowOf(parent),
      single: true
    });
  }

  // CLEAR: a union's drop keeps the clearance it has today from each partner's
  // label block and from the block of any person between the partners on its
  // row. Only what holds in the uniform chart is asked for, so the system
  // stays feasible; a plain midpoint union always clears its partners today
  // (the pitch is the widest need), which is the case that bit. A single
  // parent's drop runs through their own block, as it always has.
  const blockHalf = (n) => Math.max(0, needOf(n) - margin) / 2;
  const clearOf = (n) => blockHalf(n) + lineClear;
  const between = (ue, n) =>
    n.virtualX > ue.left.virtualX && n.virtualX < ue.right.virtualX;
  for (const ue of unionExprs) {
    if (ue.single) continue;
    const addClear = ue.soft ? addSoft : add;
    if (ue.x - ue.left.x >= clearOf(ue.left) - CHECK_EPS) {
      addClear(
        'clear',
        [...ue.terms, [idx(ue.left), -1]],
        'ge',
        clearOf(ue.left) - ue.constant
      );
    }
    if (ue.right.x - ue.x >= clearOf(ue.right) - CHECK_EPS) {
      addClear(
        'clear',
        [[idx(ue.right), 1], ...neg(ue.terms)],
        'ge',
        clearOf(ue.right) + ue.constant
      );
    }
    for (const n of rows.get(ue.row) || []) {
      if (!usable(n) || !between(ue, n)) continue;
      const k = idx(n);
      const d = ue.x - n.x;
      if (d >= clearOf(n) - CHECK_EPS) {
        addClear(
          'clear',
          [...ue.terms, [k, -1]],
          'ge',
          clearOf(n) - ue.constant
        );
      } else if (-d >= clearOf(n) - CHECK_EPS) {
        addClear(
          'clear',
          [[k, 1], ...neg(ue.terms)],
          'ge',
          clearOf(n) + ue.constant
        );
      }
    }
  }

  // ORDER census: the x-orders the drawn lines depend on, as consecutive pairs
  // of sorted sequences. Per row: its people with its droppers (unions and
  // single parents) — marriage bars and drops; per adjacent row pair: the
  // row's people and droppers with the people of the row beneath — the bars
  // and child drops at the gap's midline, and the label blocks that hang into
  // it. Each relation is { p, q, margin }: q sits right of p today and must
  // stay at least `margin` right of it — the label-block clearance where a
  // person's block and a line from the other row keep it today (33's labels
  // under 40's bar on Tim's third family), else 1 px. Two people on one row
  // are the pair constraints' business; a drop beside a person on its own row
  // cannot reach them. Coincident pairs (under 1 px) are left alone. The solve
  // adds a relation only when a solution breaks it (lazily).
  const orderRelations = [];
  const personElem = (n) => ({
    terms: [[idx(n), 1]],
    constant: 0,
    x: n.x,
    kind: 'person',
    row: rowOf(n),
    node: n
  });
  const dropperElem = (ue) => ({
    terms: ue.terms,
    constant: ue.constant || 0,
    x: ue.x,
    kind: 'dropper',
    row: ue.row,
    soft: !!ue.soft
  });
  const droppersByRow = new Map();
  for (const ue of unionExprs) {
    if (!droppersByRow.has(ue.row)) droppersByRow.set(ue.row, []);
    droppersByRow.get(ue.row).push(dropperElem(ue));
  }
  const marginOf = (p, q) => {
    if (p.kind === 'person' && q.kind === 'person') {
      if (p.row === q.row) return null; // a pair constraint already
      // The upper person's block against the lower person's drop.
      const upper = p.row < q.row ? p : q;
      const clear = clearOf(upper.node);
      return q.x - p.x >= clear - CHECK_EPS ? clear : STRAIGHT_EPS;
    }
    if (p.kind !== q.kind && p.row === q.row) return null; // a drop beside its own row
    return STRAIGHT_EPS;
  };
  const consecutive = (elems) => {
    const seq = elems.slice().sort((p, q) => p.x - q.x);
    for (let i = 1; i < seq.length; i++) {
      const p = seq[i - 1];
      const q = seq[i];
      if (q.x - p.x < STRAIGHT_EPS) continue;
      const m = marginOf(p, q);
      if (m == null) continue;
      orderRelations.push({
        p: p.terms,
        pc: p.constant,
        q: q.terms,
        qc: q.constant,
        margin: m,
        soft: !!(p.soft || q.soft)
      });
    }
  };
  for (const [r, arr] of rows) {
    const people = arr.filter(usable).map(personElem);
    const droppers = droppersByRow.get(r) || [];
    if (droppers.length) consecutive([...people, ...droppers]);
    const beneath = (rows.get(r + 1) || []).filter(usable).map(personElem);
    if (beneath.length) consecutive([...people, ...droppers, ...beneath]);
  }

  // EVEN sibship runs: full siblings consecutive on a row, nothing between,
  // three or more, with equal gaps today.
  for (const arr of rows.values()) {
    let run = [];
    const flush = () => {
      if (run.length >= 3) {
        const gaps = run.slice(1).map((n, i) => n.x - run[i].x);
        if (Math.max(...gaps) - Math.min(...gaps) <= STRAIGHT_EPS) {
          for (let j = 0; j + 2 < run.length; j++) {
            add(
              'even',
              [
                [idx(run[j]), -1],
                [idx(run[j + 1]), 2],
                [idx(run[j + 2]), -1]
              ],
              'eq',
              0
            );
          }
        }
      }
      run = [];
    };
    for (const n of arr) {
      const key = sibKey(n);
      if (
        run.length &&
        key &&
        key === sibKey(run[run.length - 1]) &&
        usable(n)
      ) {
        run.push(n);
      } else {
        flush();
        run = key && usable(n) ? [n] : [];
      }
    }
    flush();
  }

  return {
    vars,
    base,
    uniformX,
    rowOf,
    constraints,
    orderRelations,
    rows,
    nodeGap,
    grid,
    floor,
    persons
  };
}

/**
 * Solve the drawn positions for an unmapped render model.
 *
 * @returns {{ rows: Record<string, { us: number[], xs: number[] }>, slope: number } | null}
 *   the per-row map, or null when the chart needs no map (fewer than two people,
 *   or the solve reproduces the uniform layout) or when the solve could not be
 *   trusted (the caller then draws the uniform chart).
 */
export function solveColumns(model, opts = {}) {
  const ex = extractColumnConstraints(model, opts);
  if (!ex) return null;
  const {
    vars,
    base,
    uniformX,
    rowOf,
    constraints,
    orderRelations,
    rows,
    nodeGap,
    floor
  } = ex;
  const n = vars.length;
  const W = n; // the chart's right edge, one extra variable

  // Positions as P_i = base_i + s_i with s_i ≥ 0. Minimise the right edge W
  // first, the rows' total stretch second (every row's last minus first:
  // a couple shifts rather than opens, which is what moving only one partner
  // by twice the amount would otherwise tie with), and the total displacement
  // third (a row never slides for nothing). The weights keep the three
  // strictly ordered.
  const STRETCH_WEIGHT = 1e-3;
  const DISPLACEMENT_WEIGHT = 1e-6;
  const toRow = (terms, op, rhs) => {
    const coef = new Float64Array(n + 1);
    let constant = 0;
    for (const [i, alpha] of terms) {
      coef[i] += alpha;
      if (i < n) constant += alpha * base[i];
    }
    const r = rhs - constant;
    let allZero = true;
    let allNonNeg = true;
    for (let i = 0; i <= n; i++) {
      if (coef[i] !== 0) allZero = false;
      if (coef[i] < 0) allNonNeg = false;
    }
    if (allZero) {
      if (op === 'ge' && r > CHECK_EPS) return 'infeasible';
      if (op === 'eq' && Math.abs(r) > CHECK_EPS) return 'infeasible';
      return null;
    }
    if (op === 'ge' && allNonNeg && r <= 0) return null;
    return { coef, op, rhs: r };
  };
  // The BAND rule is the one constraint the uniform chart does not already
  // satisfy, so it and everything derived from it are SOFT: solved with
  // everything else first, under the chart's uniform width (the rule may move
  // a sibship, never widen the chart), and dropped — the solve re-run from
  // the start without them, which is the solve as it was — if the system
  // cannot take them.
  const hasSoft = constraints.some((c) => c.soft);
  let soft = hasSoft;
  const uniformW = Math.max(...vars.map(uniformX));
  const buildRows = () => {
    const out = [];
    for (const cst of constraints) {
      if (cst.soft && !soft) continue;
      const row = toRow(cst.terms, cst.op, cst.rhs);
      if (row === 'infeasible') {
        if (cst.soft) continue;
        return null;
      }
      if (row) out.push(row);
    }
    // W ≥ the last person of every row (the pairs keep the others left of it).
    for (const arr of rows.values()) {
      const people = arr.filter((p) => vars.includes(p));
      if (!people.length) continue;
      const last = people[people.length - 1];
      const row = toRow(
        [
          [W, 1],
          [vars.indexOf(last), -1]
        ],
        'ge',
        0
      );
      if (row) out.push(row);
    }
    if (soft) {
      const cap = toRow([[W, 1]], 'le', uniformW);
      if (cap) out.push(cap);
    }
    return out;
  };
  let lpRows = buildRows();
  if (!lpRows) return null;
  const objective = new Float64Array(n + 1).fill(DISPLACEMENT_WEIGHT);
  objective[W] = 1;
  for (const arr of rows.values()) {
    const people = arr.filter((p) => vars.includes(p));
    if (people.length < 2) continue;
    objective[vars.indexOf(people[0])] -= STRETCH_WEIGHT;
    objective[vars.indexOf(people[people.length - 1])] += STRETCH_WEIGHT;
  }

  const positionsOf = (sol) => {
    const P = new Float64Array(n);
    for (let i = 0; i < n; i++) P[i] = base[i] + Math.max(0, sol.x[i]);
    return P;
  };
  const valueAt = (terms, P) => {
    let v = 0;
    for (const [i, coef] of terms) v += coef * P[i];
    return v;
  };
  const broken = (rel, P) =>
    valueAt(rel.q, P) + rel.qc - (valueAt(rel.p, P) + rel.pc) <
    rel.margin - CHECK_EPS;

  // Lazy order rounds: solve, add every relation the solution breaks, repeat.
  // The uniform chart satisfies every hard relation, so each round stays
  // feasible once the soft rule is out.
  const MAX_ROUNDS = 6;
  const active = (rel) => !rel.soft || soft;
  const rounds = () => {
    const pending = new Set(orderRelations.filter(active));
    const activate = (rel) => {
      pending.delete(rel);
      const row = toRow(
        [...rel.q, ...negTerms(rel.p)],
        'ge',
        rel.margin - (rel.qc - rel.pc)
      );
      if (row === 'infeasible') return false;
      if (row) lpRows.push(row);
      return true;
    };
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const sol = solveLP({ n: n + 1, c: objective, constraints: lpRows });
      if (sol.status !== 'optimal') return null;
      const P = positionsOf(sol);
      const violated = [...pending].filter((rel) => broken(rel, P));
      if (!violated.length) return P;
      // The penultimate round puts everything still pending in at once.
      const toActivate = round === MAX_ROUNDS - 2 ? [...pending] : violated;
      for (const rel of toActivate) if (!activate(rel)) return null;
    }
    return null;
  };
  let P = rounds();
  if (!P && soft) {
    // The soft rule could not be met with everything else: the solve as it
    // was, from the start, without it.
    soft = false;
    lpRows = buildRows();
    if (!lpRows) return null;
    P = rounds();
  }
  if (!P) return null;

  // Verify against the constraints as extracted (the solver's own tolerance is
  // not the chart's), and every order relation.
  for (const cst of constraints) {
    if (cst.soft && !soft) continue;
    const v = valueAt(cst.terms, P);
    const tol = CHECK_EPS * (1 + Math.abs(cst.rhs));
    if (cst.op === 'ge' && v < cst.rhs - tol) return null;
    if (cst.op === 'le' && v > cst.rhs + tol) return null;
    if (cst.op === 'eq' && Math.abs(v - cst.rhs) > tol) return null;
  }
  for (const rel of orderRelations) {
    if (active(rel) && broken(rel, P)) return null;
  }

  // A solve that reproduces the uniform layout needs no map at all.
  let identity = true;
  for (let i = 0; i < n; i++) {
    if (Math.abs(P[i] - uniformX(vars[i])) > CHECK_EPS) {
      identity = false;
      break;
    }
  }
  if (identity) return null;

  // Per-row maps over the row's solved people, in uniform order. The pairs are
  // strictly increasing on both sides (the pair constraints keep the order).
  const byRow = new Map();
  vars.forEach((v, i) => {
    const r = String(rowOf(v));
    if (!byRow.has(r)) byRow.set(r, []);
    byRow.get(r).push({ u: uniformX(v), x: P[i] });
  });
  const rowsOut = {};
  for (const [r, pts] of byRow) {
    pts.sort((p, q) => p.u - q.u);
    rowsOut[r] = { us: pts.map((p) => p.u), xs: pts.map((p) => p.x) };
  }
  return { rows: rowsOut, slope: floor / nodeGap };
}
